import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  FaBuilding, 
  FaUserMd, 
  FaImages, 
  FaEnvelope, 
  FaArrowRight, 
  FaShieldAlt, 
  FaCalendarAlt, 
  FaCheckCircle 
} from 'react-icons/fa';
import './AdminDashboard.css';
import axiosInstance from '../../api/axiosInstance';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    enquiries: 0,
    faculty: 0,
    gallery: 0,
    contacts: 0
  });
  const [loading, setLoading] = useState(true);

  // Dynamic Time-Based Greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const formattedDate = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const [enquiriesRes, facultyRes, galleryRes, contactRes] = await Promise.allSettled([
          axiosInstance.get('/api/institution/all'),
          axiosInstance.get('/api/faculty/all'),
          axiosInstance.get('/api/gallery'),
          axiosInstance.get('/api/contact/all')
        ]);

        setStats({
          enquiries: enquiriesRes.status === 'fulfilled' && Array.isArray(enquiriesRes.value?.data) ? enquiriesRes.value.data.length : 0,
          faculty: facultyRes.status === 'fulfilled' && Array.isArray(facultyRes.value?.data) ? facultyRes.value.data.length : 0,
          gallery: galleryRes.status === 'fulfilled' && Array.isArray(galleryRes.value?.data) ? galleryRes.value.data.length : 0,
          contacts: contactRes.status === 'fulfilled' && Array.isArray(contactRes.value?.data) ? contactRes.value.data.length : 0
        });
      } catch (error) {
        console.error("Dashboard stats fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // ONLY 4 CORE ADMIN MODULES
  const adminModules = [
    {
      id: "01",
      title: "Institutional Enquiries",
      badge: "Project Requests",
      desc: "Manage and process new medical college establishment, MBBS/PG seat expansion advisory requests, and feasibility queries.",
      link: "/admin/institutional-enquiries",
      count: stats.enquiries,
      countLabel: "Institutional Enquiries",
      icon: <FaBuilding />,
      colorClass: "teal"
    },
    {
      id: "02",
      title: "Faculty Registrations",
      badge: "Academic Manpower",
      desc: "Review permanent and full-time medical faculty applications, verify qualifications, download CV files, and track hiring stages.",
      link: "/admin/faculty-registrations",
      count: stats.faculty,
      countLabel: "Doctor Profiles",
      icon: <FaUserMd />,
      colorClass: "green"
    },
    {
      id: "03",
      title: "Gallery & Media Management",
      badge: "Visual Showcase",
      desc: "Upload campus photos, manage laboratory showcase albums, organize folder names, and toggle public image visibility.",
      link: "/admin/add-gallery",
      count: stats.gallery,
      countLabel: "Uploaded Photos",
      icon: <FaImages />,
      colorClass: "dark"
    },
    {
      id: "04",
      title: "General Contact Messages",
      badge: "Desk Inquiries",
      desc: "Review direct client submissions, public inquiries from contact forms, and handle prompt stakeholder communication.",
      link: "/admin/view-contact",
      count: stats.contacts,
      countLabel: "Contact Messages",
      icon: <FaEnvelope />,
      colorClass: "blue"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Admin Executive Console | Nutrivio Medical Services</title>
        <meta name="description" content="Central administrative console for Nutrivio Medical Services Private Limited." />
      </Helmet>

      <div className="admin-dashboard-wrapper">
        <div className="admin-dashboard-container">
          
          {/* ================= 1. GREETING & HERO HEADER ================= */}
          <section className="admin-dashboard-hero">
            <div className="admin-dashboard-hero-content">
              
              <div className="admin-dashboard-eyebrow">
                <span className="admin-dashboard-eyebrow-dash"></span>
                <span>Nutrivio Administration Console</span>
                <span className="admin-dashboard-eyebrow-dash"></span>
              </div>

              <h1 className="admin-dashboard-greeting-title">
                {getGreeting()}, <span className="admin-dashboard-gradient-text">Administrator</span>
              </h1>

              <div className="admin-dashboard-date-strip">
                <span className="admin-dashboard-date-item">
                  <FaCalendarAlt /> {formattedDate}
                </span>
                <span className="admin-dashboard-status-pill">
                  <span className="admin-dashboard-pulse-dot"></span>
                  Console Active &amp; Operational
                </span>
              </div>

            </div>
          </section>

          {/* ================= 2. 4-STATS METRICS STRIP ================= */}
          <section className="admin-dashboard-stats-grid">
            
            {/* 1. Institutional Enquiries Stat */}
            <div className="admin-dashboard-stat-card">
              <div className="admin-dashboard-stat-icon-wrap teal">
                <FaBuilding />
              </div>
              <div className="admin-dashboard-stat-info">
                <span className="admin-dashboard-stat-label">Institutional Inquiries</span>
                <strong className="admin-dashboard-stat-value">{loading ? '...' : stats.enquiries}</strong>
              </div>
              <Link to="/admin/institutional-enquiries" className="admin-dashboard-stat-jump" title="Go to Inquiries">
                <FaArrowRight />
              </Link>
            </div>

            {/* 2. Faculty Registrations Stat */}
            <div className="admin-dashboard-stat-card">
              <div className="admin-dashboard-stat-icon-wrap green">
                <FaUserMd />
              </div>
              <div className="admin-dashboard-stat-info">
                <span className="admin-dashboard-stat-label">Faculty Candidates</span>
                <strong className="admin-dashboard-stat-value">{loading ? '...' : stats.faculty}</strong>
              </div>
              <Link to="/admin/faculty-registrations" className="admin-dashboard-stat-jump" title="Go to Faculty">
                <FaArrowRight />
              </Link>
            </div>

            {/* 3. Gallery Media Stat */}
            <div className="admin-dashboard-stat-card">
              <div className="admin-dashboard-stat-icon-wrap dark">
                <FaImages />
              </div>
              <div className="admin-dashboard-stat-info">
                <span className="admin-dashboard-stat-label">Published Media</span>
                <strong className="admin-dashboard-stat-value">{loading ? '...' : stats.gallery}</strong>
              </div>
              <Link to="/admin/add-gallery" className="admin-dashboard-stat-jump" title="Go to Gallery">
                <FaArrowRight />
              </Link>
            </div>

            {/* 4. Contact Inquiries Stat */}
            <div className="admin-dashboard-stat-card">
              <div className="admin-dashboard-stat-icon-wrap blue">
                <FaEnvelope />
              </div>
              <div className="admin-dashboard-stat-info">
                <span className="admin-dashboard-stat-label">Contact Desk Messages</span>
                <strong className="admin-dashboard-stat-value">{loading ? '...' : stats.contacts}</strong>
              </div>
              <Link to="/admin/view-contact" className="admin-dashboard-stat-jump" title="Go to Contact Inquiries">
                <FaArrowRight />
              </Link>
            </div>

          </section>

          {/* ================= 3. 4 CORE ADMIN MANAGEMENT MODULES (2x2 GRID) ================= */}
          <section className="admin-dashboard-section">
            <div className="admin-dashboard-section-header">
              <h2 className="admin-dashboard-section-title">Administrative Portals</h2>
              <p className="admin-dashboard-section-subtitle">
                Select an operational workspace to review client requests, verify medical doctors, upload media, or process contact inquiries.
              </p>
            </div>

            <div className="admin-dashboard-modules-grid">
              {adminModules.map((module) => (
                <div key={module.id} className="admin-dashboard-module-card">
                  
                  {/* Top Hover Gradient Bar */}
                  <div className="admin-dashboard-card-hover-bar"></div>

                  <div className="admin-dashboard-module-header">
                    <div className={`admin-dashboard-module-icon ${module.colorClass}`}>
                      {module.icon}
                    </div>
                    <span className="admin-dashboard-module-badge">{module.badge}</span>
                  </div>

                  <h3 className="admin-dashboard-module-title">{module.title}</h3>
                  <p className="admin-dashboard-module-desc">{module.desc}</p>

                  <div className="admin-dashboard-module-meta">
                    <span className="admin-dashboard-meta-count">
                      <strong>{loading ? '...' : module.count}</strong> {module.countLabel}
                    </span>
                  </div>

                  <div className="admin-dashboard-module-footer">
                    <Link to={module.link} className="admin-dashboard-module-btn">
                      <span>Open Workspace</span>
                      <FaArrowRight />
                    </Link>
                  </div>

                </div>
              ))}
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default AdminDashboard;